// src/web/client/src/components/automations/RetainEmailCards.jsx
//
// The two email types this app sends on its own — the thank-you after an order
// and the review request after delivery — as one card each (UX brief, B3).
// Switch + timing + the last 30 days' numbers (GET /analytics/messages, the
// same per-type figures Results shows) + a link to the template. The review
// card also carries the delay, and — on the Platform plan — the brain's review
// requests: its switch turns BOTH off, so one switch really means "no review
// requests", and its "Requested" count and recent list sit under the card.
//
// The switch must mean what it shows. This app only sends a type that has an
// ACTIVE template, so a card whose type has none shows "Needs setup" (no
// template) or "Off in Templates" (template switched off there) instead of a
// switch — the settings default ON, and a switch reading ON while nothing is
// sent was the lie. Turning a type ON (the switch, or "Set up and turn on")
// calls POST /templates/turn-on/:type, which creates the default template
// ACTIVE when there is none — that click is the merchant's consent — and sets
// the setting. Turning OFF only writes the setting, as before.

import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { safeApi } from '@/utils/api';
import { API_BASE_URL } from '@/constants';
import { authenticatedFetch } from '@/utils/authenticatedFetch';
import EmailTypeCard from './EmailTypeCard';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// Last-30-days numbers per type. null while loading or when the plan has no
// analytics — the card then shows dashes, never zeros it cannot vouch for.
export function useTypeStats() {
  const [perf, setPerf] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await safeApi.get('/analytics/messages?period=30d');
      if (alive) setPerf(r && r.success && r.message_performance ? r.message_performance : (r?.data?.message_performance || null));
    })();
    return () => { alive = false; };
  }, []);
  return perf;
}

const count = (n) => Number(n || 0).toLocaleString('en-US');
export function statsFor(perf, type) {
  const s = perf?.[type];
  return [
    { label: 'Sent', value: s ? count(s.sent) : undefined },
    { label: 'Opened', value: s ? count(s.opened) : undefined },
    { label: 'Revenue after', value: s ? money(s.revenue) : undefined },
  ];
}

// Per-type template state: 'active' | 'inactive' | 'none', or null while
// loading / when the list cannot be read — the card then shows its plain
// switch, exactly as before this state existed.
const TEMPLATE_TYPES = ['thank_you', 'review_request'];
export function useTemplateStates(domain) {
  const [states, setStates] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!domain) return undefined;
    (async () => {
      try {
        const res = await authenticatedFetch(`${API_BASE_URL}/templates?shop_domain=${encodeURIComponent(domain)}`);
        const list = res.ok ? await res.json().catch(() => null) : null;
        if (!alive || !Array.isArray(list)) return;
        const next = {};
        for (const type of TEMPLATE_TYPES) {
          const ofType = list.filter((t) => t.type === type);
          next[type] = ofType.some((t) => t.is_active) ? 'active' : ofType.length ? 'inactive' : 'none';
        }
        setStates(next);
      } catch {
        // Unknown state → plain switch.
      }
    })();
    return () => { alive = false; };
  }, [domain]);
  const setState = (type, state) => setStates((prev) => ({ ...(prev || {}), [type]: state }));
  return { states, setState };
}

// Turn a type ON through the server (see header). On success the setting the
// server wrote is merged locally and the card takes the returned state.
export function useTurnOn({ applyLocal, setState }) {
  const { toast } = useToast();
  const [turningOn, setTurningOn] = useState(null);
  const turnOn = async (type, setting, what) => {
    setTurningOn(type);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/templates/turn-on/${type}`, { method: 'POST' });
      const body = res.ok ? await res.json().catch(() => null) : null;
      if (!body?.success) throw new Error(`HTTP ${res.status}`);
      applyLocal({ [setting]: true });
      setState(type, body.template_state);
      return true;
    } catch (err) {
      toast({ title: `Could not turn on ${what}`, description: 'Please try again.', variant: 'destructive' });
      return false;
    } finally {
      setTurningOn(null);
    }
  };
  return { turnOn, turningOn };
}

const TEMPLATES_TAB = '/automations?tab=templates';
function setupFor(state, { what, onTurnOn, busy, disabled }) {
  if (state === 'none') {
    return {
      status: 'Needs setup',
      title: `Not sending — no ${what} is set up yet.`,
      body: 'Set up adds our ready-made email and turns it on. You can change the wording any time in Templates.',
      action: { label: busy ? 'Setting up…' : 'Set up and turn on', onClick: onTurnOn, disabled: busy || disabled },
      secondary: { label: 'Write my own', to: TEMPLATES_TAB },
    };
  }
  if (state === 'inactive') {
    return {
      status: 'Off in Templates',
      title: `Not sending — your ${what} is switched off in Templates.`,
      body: 'Switch it on there to start sending.',
      secondary: { label: 'Open Templates', to: TEMPLATES_TAB },
    };
  }
  return null;
}

function useSave(update) {
  const { toast } = useToast();
  return async (patch, what) => {
    const r = await update(patch);
    if (!r.ok) toast({ title: `Could not save ${what}`, description: r.error?.message || 'Please try again.', variant: 'destructive' });
    return r.ok;
  };
}

export function ThankYouCard({ settings, update, saving, perf, templateState = null, onTurnOn, turningOn = false }) {
  const save = useSave(update);
  const setup = setupFor(templateState, { what: 'thank-you email', onTurnOn, busy: turningOn, disabled: saving || !settings });
  const onToggle = (v) => (v && onTurnOn ? onTurnOn() : save({ thank_you_enabled: v }, 'the thank-you switch'));
  return (
    <EmailTypeCard
      testId="card-thank-you"
      title="Thank you"
      description="A thank-you email after each order, written for that customer and order."
      enabled={settings?.thank_you_enabled !== false}
      switchDisabled={saving || !settings || turningOn}
      onToggle={onToggle}
      setup={setup}
      timing="Sent shortly after the order is placed."
      stats={statsFor(perf, 'thank_you')}
      statsNote="Last 30 days. “Revenue after” is from orders that followed an email — a correlation, not proof the email caused them."
      links={setup ? [] : [{ label: 'Edit the email', to: TEMPLATES_TAB }]}
    />
  );
}

export function ReviewRequestCard({ settings, update, saving, perf, hasBrain = false, brainRequested, children, templateState = null, onTurnOn, turningOn = false }) {
  const save = useSave(update);
  const [delay, setDelay] = useState(settings?.review_delay_days ?? 7);
  useEffect(() => { setDelay(settings?.review_delay_days ?? 7); }, [settings?.review_delay_days]);

  const enabled = settings?.review_request_enabled !== false && (!hasBrain || settings?.platform_emails?.review !== false);
  // On the Platform plan the brain sends the review requests and this app's
  // own template is never used (utils/platformManaged.js) — no setup state,
  // and the switch keeps writing both sides in one save.
  const setup = hasBrain ? null : setupFor(templateState, { what: 'review request email', onTurnOn, busy: turningOn, disabled: saving || !settings });
  const onToggle = (v) => {
    if (v && !hasBrain && onTurnOn) return onTurnOn();
    return save(
      hasBrain ? { review_request_enabled: v, platform_emails: { review: v } } : { review_request_enabled: v },
      'the review-request switch',
    );
  };
  const commitDelay = () => {
    const n = Math.min(90, Math.max(0, Number.isFinite(Number(delay)) ? Math.round(Number(delay)) : 7));
    setDelay(n);
    if (n !== (settings?.review_delay_days ?? 7)) save({ review_delay_days: n }, 'the review delay');
  };

  const stats = statsFor(perf, 'review_request');
  if (hasBrain) stats.push({ label: 'Requested', value: brainRequested == null ? undefined : count(brainRequested), hint: 'Repeat customers picked for a review' });

  return (
    <EmailTypeCard
      testId="card-review-request"
      title="Review request"
      description={hasBrain
        ? 'Asks a customer for a review after delivery — and asks happy repeat customers for a review of what they keep buying. The email links to the product page on your store; nothing here collects or posts reviews.'
        : 'Asks a customer for a review after delivery. The email links to your store; nothing here collects or posts reviews.'}
      enabled={enabled}
      switchDisabled={saving || !settings || turningOn}
      onToggle={onToggle}
      setup={setup}
      timing={`Sent ${delay === 0 ? 'on the day of delivery' : `${delay} day${delay === 1 ? '' : 's'} after delivery`} — at the customer’s usual open time when Smart timing is on (Timing tab).`}
      stats={stats}
      statsNote="Last 30 days."
      links={setup ? [] : [{ label: 'Edit the email', to: TEMPLATES_TAB }]}
    >
      <div className="flex items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="review_delay">Days after delivery</Label>
          <Input id="review_delay" type="number" min="0" max="90" step="1" className="w-28" value={delay}
            onChange={(e) => setDelay(e.target.value)} onBlur={commitDelay}
            onKeyDown={(e) => { if (e.key === 'Enter') commitDelay(); }} disabled={saving || !settings} />
        </div>
        <p className="text-xs text-gray-500 pb-2">0–90. 0 asks on the day of delivery.</p>
      </div>
      {children}
    </EmailTypeCard>
  );
}
